import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.15) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const ChevronDown = ({ open }) => (
  <svg
    className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const Header = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
      if (aboutRef.current && !aboutRef.current.contains(e.target)) setAboutOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-150 ${
      isActive ? 'text-gold-500' : 'text-slate-200 hover:text-gold-400'
    }`;

  const closeAll = () => {
    setServicesOpen(false);
    setAboutOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="fixed w-full top-0 z-50 bg-navy-800 border-b border-navy-700 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="shrink-0" onClick={closeAll}>
            <img
              src="/dtx_header.svg"
              alt="DTX Digital Transformation — Consultancy · Contractor"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/" className={navLinkClass} end>
              Home
            </NavLink>

            {/* Services dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                className="flex items-center gap-1 text-sm font-medium text-slate-200 hover:text-gold-400 transition-colors"
                onClick={() => { setServicesOpen(!servicesOpen); setAboutOpen(false); }}
              >
                Services
                <ChevronDown open={servicesOpen} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-3 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 min-w-[300px]">
                  {[
                    { to: '/services/facilities', label: 'Facilities Management', sub: 'Space Planning & Workplace Solutions' },
                    { to: '/services/it', label: 'IT & Cybersecurity', sub: 'Cloud, DevSecOps & Advanced Security' },
                    { to: '/services/grc', label: 'Governance, Risk & Compliance', sub: 'Audit Readiness, PQC & Continuous Compliance' },
                    { to: '/services/pm-pmo', label: 'Program & Project Management', sub: 'PMO Standup, IV&V & Project Rescue' },
                    { to: '/services/real-property', label: 'Federal Real Property Disposal', sub: 'FMR § 102-75 Compliance & GSA Coordination' },
                  ].map(({ to, label, sub }) => (
                    <Link
                      key={to}
                      to={to}
                      className="block px-5 py-3 text-sm text-navy-800 hover:bg-slate-50 hover:text-gold-600 font-medium transition-colors"
                      onClick={closeAll}
                    >
                      <span className="block font-semibold">{label}</span>
                      <span className="block text-xs text-slate-400 mt-0.5">{sub}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* About dropdown */}
            <div ref={aboutRef} className="relative">
              <button
                className="flex items-center gap-1 text-sm font-medium text-slate-200 hover:text-gold-400 transition-colors"
                onClick={() => { setAboutOpen(!aboutOpen); setServicesOpen(false); }}
              >
                About
                <ChevronDown open={aboutOpen} />
              </button>
              {aboutOpen && (
                <div className="absolute top-full left-0 mt-3 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50">
                  <Link
                    to="/about/leadership-team"
                    className="block px-5 py-3 text-sm text-navy-800 hover:bg-slate-50 hover:text-gold-600 font-medium transition-colors"
                    onClick={closeAll}
                  >
                    Leadership Team
                  </Link>
                  <Link
                    to="/about/capability-statement"
                    className="block px-5 py-3 text-sm text-navy-800 hover:bg-slate-50 hover:text-gold-600 font-medium transition-colors"
                    onClick={closeAll}
                  >
                    Capability Statement
                  </Link>
                  <Link
                    to="/about/naics-codes"
                    className="block px-5 py-3 text-sm text-navy-800 hover:bg-slate-50 hover:text-gold-600 font-medium transition-colors"
                    onClick={closeAll}
                  >
                    NAICS Codes
                  </Link>
                </div>
              )}
            </div>

            <NavLink to="/contact" className={navLinkClass}>
              Contact Us
            </NavLink>

            <Link
              to="/contact"
              className="bg-gold-500 hover:bg-gold-400 text-navy-900 px-5 py-2.5 rounded-lg text-sm font-bold transition-colors shadow-md shadow-gold-500/20"
              onClick={closeAll}
            >
              Get in Touch
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-white p-2 rounded-lg hover:bg-navy-700 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-navy-900 border-t border-navy-700 px-4 py-4 space-y-1">
          <Link to="/" className="block py-2.5 px-3 text-slate-200 hover:text-gold-400 font-medium rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
            Home
          </Link>
          <div className="py-1">
            <p className="px-3 py-1.5 text-gold-500 text-xs font-bold uppercase tracking-wider">Services</p>
            <Link to="/services/facilities" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              Facilities Management & Space Planning
            </Link>
            <Link to="/services/it" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              IT & Cybersecurity
            </Link>
            <Link to="/services/grc" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              Governance, Risk & Compliance
            </Link>
            <Link to="/services/pm-pmo" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              Program & Project Management
            </Link>
            <Link to="/services/real-property" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              Federal Real Property Disposal
            </Link>
          </div>
          <div className="py-1">
            <p className="px-3 py-1.5 text-gold-500 text-xs font-bold uppercase tracking-wider">About</p>
            <Link to="/about/leadership-team" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              Leadership Team
            </Link>
            <Link to="/about/capability-statement" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              Capability Statement
            </Link>
            <Link to="/about/naics-codes" className="block py-2.5 px-6 text-slate-300 hover:text-gold-400 text-sm rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
              NAICS Codes
            </Link>
          </div>
          <Link to="/contact" className="block py-2.5 px-3 text-slate-200 hover:text-gold-400 font-medium rounded-lg hover:bg-navy-800 transition-colors" onClick={closeAll}>
            Contact Us
          </Link>
          <div className="pt-3">
            <Link to="/contact" className="block w-full text-center bg-gold-500 hover:bg-gold-400 text-navy-900 px-5 py-3 rounded-xl font-bold transition-colors" onClick={closeAll}>
              Get in Touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export { GRID_BG };
export default Header;
